import { projectApi, type Project } from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormError } from '@/hooks/use-form-errors';
import { useSession } from '@/hooks/use-session';
import { ConfirmDialog } from '@/pages/settings/components/confirm-dialog';
import { SettingsSection } from '@/pages/settings/components/settings-section';
import { GoalsSection } from './components/goals-section';
import { MonitoringTermsSection } from './components/monitoring-terms-section';

const ProjectDetailsForm = ({
  project,
  canManage,
}: {
  project: Project;
  canManage: boolean;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [saved, setSaved] = React.useState(false);

  const mutation = useMutation({
    mutationFn: (body: {
      name: string;
      primaryDomain: string | null;
      allowedHostnames: string[] | null;
      timezone: string;
    }) => projectApi.updateProject(project.id, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(['project', project.id], updated);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: captureError,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManage) {
      return;
    }

    clearError();
    setSaved(false);
    const form = new FormData(event.currentTarget);
    mutation.mutate({
      name: String(form.get('name')).trim(),
      primaryDomain: String(form.get('primaryDomain') || '').trim() || null,
      allowedHostnames:
        String(form.get('allowedHostnames') || '')
          .split(/[\s,]+/)
          .map((value) => value.trim())
          .filter(Boolean) || null,
      timezone: String(form.get('timezone')).trim(),
    });
  };

  return (
    <SettingsSection
      title="Identity and reporting"
      description="These values identify this project throughout GainSignal and anchor attribution."
      onSubmit={handleSubmit}
      footer={
        canManage ? (
          <>
            {saved ? (
              <span className="mr-auto text-sm text-positive">Saved</span>
            ) : null}
            <Button type="submit" loading={mutation.isPending}>
              Save changes
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {!canManage ? (
          <Alert>
            You need organization admin access to edit this project.
          </Alert>
        ) : null}
        {error ? <Alert tone="error">{error}</Alert> : null}

        {canManage && (
          <Button asChild variant="secondary">
            <Link to={`/projects/${project.id}/setup?edit=1`}>
              Edit product profile and platform
            </Link>
          </Button>
        )}
        <Field label="Name" htmlFor="project-name">
          <Input
            id="project-name"
            name="name"
            required
            defaultValue={project.name}
            disabled={!canManage}
          />
        </Field>

        <Field
          label="Primary domain"
          htmlFor="project-domain"
          hint="Used as the default site for attribution and reporting."
        >
          <Input
            id="project-domain"
            name="primaryDomain"
            placeholder="example.com"
            defaultValue={project.primaryDomain ?? ''}
            disabled={!canManage}
          />
        </Field>

        <Field
          label="Allowed analytics hostnames"
          htmlFor="project-allowed-hostnames"
          hint="Optional. Separate hostnames with commas; use *.example.com for subdomains. Empty allows all hosts."
        >
          <Input
            id="project-allowed-hostnames"
            name="allowedHostnames"
            placeholder="example.com, *.example.com"
            defaultValue={project.allowedHostnames?.join(', ') ?? ''}
            disabled={!canManage}
          />
        </Field>

        <Field
          label="Reporting timezone"
          htmlFor="project-timezone"
          hint="Use an IANA timezone such as Europe/Zurich or UTC."
        >
          <Input
            id="project-timezone"
            name="timezone"
            required
            defaultValue={project.timezone}
            disabled={!canManage}
          />
        </Field>
      </div>
    </SettingsSection>
  );
};

export const ProjectDetailsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.getProject(projectId!),
    enabled: Boolean(projectId),
  });
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: projectApi.listProjects,
  });
  const role = user?.currentOrganization.role;
  const canManage = role === 'owner' || role === 'admin';

  const deleteMutation = useMutation({
    mutationFn: () => projectApi.deleteProject(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/', { replace: true });
    },
  });

  if (projectQuery.isLoading) {
    return <Skeleton className="mx-auto h-[420px] max-w-2xl rounded-card" />;
  }

  if (!projectQuery.data) {
    return (
      <Card className="mx-auto max-w-2xl shadow-none">
        <EmptyState
          icon={SlidersHorizontal}
          title="Project not found"
          description="It may have been deleted or belong to another organization."
          action={
            <Button asChild variant="secondary">
              <Link to="/">Back to projects</Link>
            </Button>
          }
        />
      </Card>
    );
  }

  const project = projectQuery.data;
  const canDelete = canManage && (projectsQuery.data?.projects.length ?? 0) > 1;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          Project details
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          Identity, attribution defaults, and lifecycle for {project.name}.
        </p>
      </div>

      <ProjectDetailsForm
        key={`${project.id}:${project.name}:${project.primaryDomain}:${project.allowedHostnames?.join(',')}:${project.timezone}`}
        project={project}
        canManage={canManage}
      />

      <GoalsSection projectId={project.id} canManage={canManage} />

      <MonitoringTermsSection projectId={project.id} canManage={canManage} />

      {canManage ? (
        <SettingsSection
          title="Delete project"
          description={
            canDelete
              ? 'Remove this project from GainSignal. Historical data is retained but no longer reachable.'
              : 'An organization must keep at least one project.'
          }
          footer={
            <Button
              type="button"
              variant="danger"
              disabled={!canDelete}
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 />
              Delete project
            </Button>
          }
        />
      ) : null}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${project.name}?`}
        description="Its API keys stop working and it disappears from the product. This cannot be undone from the dashboard."
        confirmLabel="Delete project"
        confirmationPhrase={project.name}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
};
